// DashboardsPage — left-sidebar folder explorer for dashboards.
//
// Layout mirrors `TagsPage` (sidebar + main pane in a flex row). Selected
// folder / dashboard live in the URL path so browser back / refresh land
// the user back on the same node:
//
//   /dashboards/<dashboardPath>
//   /dashboards/f/<folderPath>
//
// The nav rail links at the bare `/dashboards` home, so the last selection is
// also remembered in localStorage and reopened when the user comes back to it
// with nothing selected (`useExplorerLastVisited`).
//
// Context-menu actions, rename, move, and delete are routed through a
// single `DashboardSidebarAction` discriminator so the sidebar stays
// presentational and this page stays the single source of truth for
// which mutation runs and which dialog opens.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LayoutDashboard, Loader2 } from 'lucide-react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  useCopyDashboardFolderMutation,
  useCopyDashboardMutation,
  useCreateDashboardFolderMutation,
  useCreateDashboardMutation,
  useDeleteDashboardFolderByPathMutation,
  useDeleteDashboardFolderMutation,
  useDeleteDashboardMutation,
  useGetAuthzMeQuery,
  useListDashboardFoldersQuery,
  useListDashboardsQuery,
  useMoveDashboardFolderMutation,
  useRenameDashboardFolderMutation,
  useRenameDashboardMutation,
  useRestoreDashboardFolderMutation,
  type Dashboard,
  type DashboardFolder
} from '@axilon-apps/domains-ontology-ui-state'

import { useChatAppContext } from '../../chat-app-context'
import { RequestAccessPanel } from '../../components/ResourceSharingDialog'
import {
  ActionErrorDialog,
  countPrefixDescendants,
  CreateItemDialog,
  DeleteItemDialog,
  expandAncestors,
  hasTextSelection,
  isPathPrefix,
  joinUnderParent,
  leafSegment,
  MoveItemDialog,
  parentPathOf,
  PASTE_REFUSED,
  readMutationError,
  relocateUnderPrefix,
  relocatedCreatedPaths,
  RenameItemDialog,
  resolvePasteTarget,
  resolveUndo,
  snapshotFolderDelete,
  subtreeFolderPaths,
  useExplorerClipboard,
  useExplorerUndo,
  type CreateItemKind,
  type DeleteItemKind,
  type MoveItemKind,
  type RenameItemKind
} from '../../components/explorer'
import {
  buildDashboardsAppContextEntries,
  DASHBOARDS_SUPERSEDED_PARAMS
} from './dashboards-app-context'
import type { DashboardsOutletContext } from './DashboardsHomePage'
import DashboardEditor, { STARTER_CODE } from './editor'
import type { EditorMode } from './editor/editor-mode'
import DashboardTreeSidebar, {
  type DashboardSelection,
  type DashboardSidebarAction
} from './DashboardTreeSidebar'
import {
  AGENT_INSIGHTS_FOLDER_PATH,
  DASHBOARDS_HOME,
  dashboardFolderHref,
  dashboardHref,
  isAgentInsightsFolderPath
} from './explorer-paths'
import { legacyAgentInsightsHref } from './legacy-agent-insights'
import type { LastVisitedSelection } from './last-visited'
import { useExplorerLastVisited } from './useExplorerLastVisited'
import { useExplorerSelection } from './useExplorerSelection'

interface CreateDialogState {
  kind: CreateItemKind
  parentPath: string
}

interface RenameDialogState {
  kind: RenameItemKind
  path: string
  name: string
  folder?: DashboardFolder
  dashboard?: Dashboard
}

interface DeleteDialogState {
  kind: DeleteItemKind
  path: string
  name: string
  folder?: DashboardFolder
  dashboard?: Dashboard
}

interface MoveDialogState {
  kind: MoveItemKind
  path: string
  name: string
  dashboard?: Dashboard
}

const DashboardsPage = () => {
  const navigate = useNavigate()
  const { tabsSlot } = useOutletContext<DashboardsOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const routeSelection = useExplorerSelection('dashboards')
  const canAutogenerate = useGetAuthzMeQuery().data?.permissions.includes('ingest/read') === true

  const foldersQuery = useListDashboardFoldersQuery()
  const dashboardsQuery = useListDashboardsQuery()
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data])
  const dashboards = useMemo(() => dashboardsQuery.data ?? [], [dashboardsQuery.data])
  const isLoading = foldersQuery.isLoading || dashboardsQuery.isLoading

  const [createFolder] = useCreateDashboardFolderMutation()
  const [createDashboard] = useCreateDashboardMutation()
  const [copyDashboard] = useCopyDashboardMutation()
  const [copyFolder] = useCopyDashboardFolderMutation()
  const [restoreFolder] = useRestoreDashboardFolderMutation()
  const [renameFolder] = useRenameDashboardFolderMutation()
  const [renameDashboard] = useRenameDashboardMutation()
  const [deleteFolder] = useDeleteDashboardFolderMutation()
  const [deleteFolderByPath] = useDeleteDashboardFolderByPathMutation()
  const [moveFolder] = useMoveDashboardFolderMutation()
  const [deleteDashboard] = useDeleteDashboardMutation()

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [filter, setFilter] = useState('')
  const [createDialog, setCreateDialog] = useState<CreateDialogState | null>(null)
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null)
  const [moveDialog, setMoveDialog] = useState<MoveDialogState | null>(null)
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null)
  const clipboard = useExplorerClipboard()
  const undo = useExplorerUndo()
  const pasteInFlight = useRef(false)
  const undoInFlight = useRef(false)

  useEffect(() => {
    if (isLoading) return
    const legacyDashboardId = searchParams.get('dashboard')
    const legacyFolder = searchParams.get('folder')
    if (legacyDashboardId === null && legacyFolder === null) return

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('dashboard')
    nextParams.delete('folder')
    nextParams.delete('view')
    const search = nextParams.toString()
    const suffix = search === '' ? '' : `?${search}`

    if (legacyDashboardId) {
      const legacyInsightsHref = legacyAgentInsightsHref(legacyDashboardId)
      if (legacyInsightsHref) {
        navigate(legacyInsightsHref, { replace: true })
        return
      }
      const found = dashboards.find((d) => d.id === legacyDashboardId)
      if (found) {
        navigate(`${dashboardHref(found.path)}${suffix}`, { replace: true })
        return
      }
      setSearchParams(nextParams, { replace: true })
      return
    }

    if (legacyFolder) {
      navigate(`${dashboardFolderHref(legacyFolder)}${suffix}`, { replace: true })
      return
    }

    setSearchParams(nextParams, { replace: true })
  }, [isLoading, searchParams, dashboards, navigate, setSearchParams])

  const legacyParamsPending =
    searchParams.get('dashboard') !== null ||
    searchParams.get('folder') !== null ||
    searchParams.get('view') !== null

  const lastVisitedHref = useCallback(
    (remembered: LastVisitedSelection): string | null => {
      if (remembered.kind === 'folder') {
        return folders.some((row) => row.path === remembered.path)
          ? dashboardFolderHref(remembered.path)
          : null
      }
      return dashboards.some((row) => row.path === remembered.path)
        ? dashboardHref(remembered.path)
        : null
    },
    [folders, dashboards]
  )

  useExplorerLastVisited({
    surface: 'dashboards',
    selection: routeSelection,
    ready: !isLoading,
    paused: legacyParamsPending,
    hrefFor: lastVisitedHref
  })

  const selectedDashboard = useMemo(() => {
    if (routeSelection.kind !== 'item') return undefined
    return dashboards.find((d) => d.path === routeSelection.path)
  }, [routeSelection, dashboards])

  // The virtual Agent Insights findings leaves are gone; their deep links
  // now live on the Problems / Tasks pages. A real row at the same path
  // (an agent-linked dashboard) still wins.
  const legacyInsightsRedirect = useMemo(() => {
    if (isLoading || routeSelection.kind !== 'item') return null
    if (dashboards.some((d) => d.path === routeSelection.path)) return null
    return legacyAgentInsightsHref(routeSelection.path)
  }, [isLoading, routeSelection, dashboards])

  useEffect(() => {
    if (legacyInsightsRedirect) navigate(legacyInsightsRedirect, { replace: true })
  }, [legacyInsightsRedirect, navigate])

  const deniedDashboardPath =
    !isLoading && routeSelection.kind === 'item' && !selectedDashboard && !legacyInsightsRedirect
      ? routeSelection.path
      : undefined

  const selection: DashboardSelection = useMemo(() => {
    if (routeSelection.kind === 'item' && selectedDashboard) {
      return { kind: 'dashboard', id: selectedDashboard.id }
    }
    if (routeSelection.kind === 'folder') {
      return { kind: 'folder', path: routeSelection.path }
    }
    return { kind: 'none' }
  }, [routeSelection, selectedDashboard])

  const defaultParentForHeaderCreate = useMemo(() => {
    const unlocked = (path: string) => (isAgentInsightsFolderPath(path) ? '' : path)
    if (selection.kind === 'folder') return unlocked(selection.path)
    if (selection.kind === 'dashboard') {
      const selected = dashboards.find((d) => d.id === selection.id)
      if (selected) return unlocked(parentPathOf(selected.path))
    }
    return ''
  }, [selection, dashboards])

  const [editorMode, setEditorMode] = useState<EditorMode>('render')

  useChatAppContext(
    buildDashboardsAppContextEntries({
      view: 'dashboards',
      item: selectedDashboard,
      editorMode: selectedDashboard ? editorMode : undefined,
      folder: selection.kind === 'folder' ? selection.path : undefined
    }),
    DASHBOARDS_SUPERSEDED_PARAMS
  )

  const handleToggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const handleSelectFolder = (path: string) => {
    navigate(dashboardFolderHref(path), { replace: true })
  }

  const handleSelectDashboard = (id: string) => {
    if (id === '') {
      navigate(DASHBOARDS_HOME, { replace: true })
      return
    }
    const found = dashboards.find((d) => d.id === id)
    if (!found) return
    navigate(dashboardHref(found.path), { replace: true })
  }

  const goHomeIfUnder = (prefix: string) => {
    if (selection.kind === 'folder' && isPathPrefix(prefix, selection.path)) {
      navigate(DASHBOARDS_HOME, { replace: true })
    }
    if (selectedDashboard && isPathPrefix(prefix, selectedDashboard.path)) {
      navigate(DASHBOARDS_HOME, { replace: true })
    }
  }

  const afterFolderMoved = (fromPath: string, toPath: string) => {
    setExpanded((prev) => expandAncestors(prev, toPath))
    if (selection.kind === 'folder' && isPathPrefix(fromPath, selection.path)) {
      navigate(dashboardFolderHref(relocateUnderPrefix(fromPath, toPath, selection.path)), {
        replace: true
      })
    }
    if (selectedDashboard && isPathPrefix(fromPath, selectedDashboard.path)) {
      navigate(dashboardHref(relocateUnderPrefix(fromPath, toPath, selectedDashboard.path)), {
        replace: true
      })
    }
  }

  const moveFolderToParent = async (fromPath: string, toParentPath: string, name?: string) => {
    const toPath = joinUnderParent(toParentPath, leafSegment(fromPath))
    if (toPath === fromPath) return
    const displayName =
      name ?? folders.find((row) => row.path === fromPath)?.name ?? leafSegment(fromPath)
    const folderId = folders.find((row) => row.path === fromPath)?.id
    await moveFolder({ fromPath, toPath, ...(name !== undefined ? { name } : {}) }).unwrap()
    undo.push({
      op: 'folder-relocate',
      label: `Undo move of "${displayName}"`,
      ...(folderId !== undefined ? { folderId } : {}),
      atPath: toPath,
      toPath: fromPath,
      toName: displayName
    })
    afterFolderMoved(fromPath, toPath)
  }

  const moveDashboardToParent = async (dashboard: Dashboard, toParentPath: string) => {
    const toPath = joinUnderParent(toParentPath, leafSegment(dashboard.path))
    if (toPath === dashboard.path) return
    const updated = await renameDashboard({ id: dashboard.id, input: { path: toPath } }).unwrap()
    undo.push({
      op: 'dashboard-relocate',
      label: `Undo move of "${dashboard.name}"`,
      id: dashboard.id,
      atPath: updated.path,
      toPath: dashboard.path,
      toName: dashboard.name
    })
    setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
    navigate(dashboardHref(updated.path), { replace: true })
  }

  const handlePaste = async (destPath: string) => {
    if (pasteInFlight.current) return
    const result = resolvePasteTarget(clipboard.entry, destPath, folders, dashboards, {
      isLockedPath: isAgentInsightsFolderPath
    })
    if (result.kind === 'noop') return
    if (result.kind === 'refused') {
      if (result.reason === PASTE_REFUSED.stale) clipboard.clear()
      setActionError({ title: "Couldn't paste", message: result.reason })
      return
    }
    pasteInFlight.current = true
    try {
      if (result.kind === 'move') {
        if (result.type === 'folder') {
          await moveFolderToParent(result.fromPath, result.toParentPath)
        } else {
          const dashboard = dashboards.find((row) => row.id === result.id)
          if (!dashboard) {
            clipboard.clear()
            setActionError({ title: "Couldn't paste", message: PASTE_REFUSED.stale })
            return
          }
          await moveDashboardToParent(dashboard, result.toParentPath)
        }
        clipboard.clear()
        return
      }
      if (result.type === 'folder') {
        const createdPaths = relocatedCreatedPaths(
          result.fromPath,
          result.toPath,
          folders,
          dashboards
        )
        const copied = await copyFolder({
          fromPath: result.fromPath,
          toPath: result.toPath,
          name: result.name
        }).unwrap()
        undo.push({
          op: 'remove-folder',
          label: `Undo copy of "${result.name}"`,
          atPath: copied.toPath,
          createdPaths
        })
        setExpanded((prev) => expandAncestors(prev, copied.toPath))
        handleSelectFolder(copied.toPath)
        return
      }
      const source = dashboards.find((row) => row.id === result.id)
      const copied = await copyDashboard({
        id: result.id,
        input: { name: result.name, path: result.path }
      }).unwrap()
      undo.push({
        op: 'remove-dashboard',
        label: `Undo copy of "${copied.name}"`,
        id: copied.id,
        atPath: copied.path,
        expectCode: source?.code ?? copied.code
      })
      setExpanded((prev) => expandAncestors(prev, parentPathOf(copied.path)))
      navigate(dashboardHref(copied.path), { replace: true })
    } catch (err) {
      setActionError({ title: "Couldn't paste", message: readMutationError(err) })
    } finally {
      pasteInFlight.current = false
    }
  }
  const handlePasteRef = useRef(handlePaste)
  handlePasteRef.current = handlePaste

  const handleUndo = async () => {
    if (undoInFlight.current) return
    while (true) {
      const entry = undo.pop()
      if (entry === undefined) return
      const result = resolveUndo(entry, folders, dashboards)
      if (result.kind === 'stale') continue
      if (result.kind === 'blocked') {
        setActionError({ title: "Couldn't undo", message: result.reason })
        return
      }
      undoInFlight.current = true
      try {
        const applied = result.entry
        if (applied.op === 'folder-relocate') {
          if (
            applied.atPath === applied.toPath &&
            applied.folderId &&
            applied.toName !== undefined
          ) {
            await renameFolder({ id: applied.folderId, input: { name: applied.toName } }).unwrap()
          } else {
            await moveFolder({
              fromPath: applied.atPath,
              toPath: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }).unwrap()
            afterFolderMoved(applied.atPath, applied.toPath)
          }
          return
        }
        if (applied.op === 'dashboard-relocate') {
          const updated = await renameDashboard({
            id: applied.id,
            input: {
              path: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }
          }).unwrap()
          setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
          navigate(dashboardHref(updated.path), { replace: true })
          return
        }
        if (applied.op === 'remove-dashboard') {
          await deleteDashboard(applied.id).unwrap()
          if (selection.kind === 'dashboard' && selection.id === applied.id) {
            navigate(DASHBOARDS_HOME, { replace: true })
          }
          return
        }
        if (applied.op === 'remove-folder') {
          await deleteFolderByPath({ path: applied.atPath }).unwrap()
          goHomeIfUnder(applied.atPath)
          return
        }
        if (applied.op === 'restore-dashboard') {
          const created = await createDashboard({
            name: applied.name,
            path: applied.path,
            code: applied.code,
            ...(applied.writebackToolIds && applied.writebackToolIds.length > 0
              ? { writebackToolIds: [...applied.writebackToolIds] }
              : {})
          }).unwrap()
          setExpanded((prev) => expandAncestors(prev, parentPathOf(created.path)))
          navigate(dashboardHref(created.path), { replace: true })
          return
        }
        await restoreFolder({
          folders: [...applied.folders],
          dashboards: applied.dashboards.map((row) => ({
            name: row.name,
            path: row.path,
            code: row.code,
            ...(row.writebackToolIds && row.writebackToolIds.length > 0
              ? { writebackToolIds: [...row.writebackToolIds] }
              : {})
          }))
        }).unwrap()
        setExpanded((prev) => expandAncestors(prev, applied.rootPath))
        handleSelectFolder(applied.rootPath)
        return
      } catch (err) {
        setActionError({ title: "Couldn't undo", message: readMutationError(err) })
        return
      } finally {
        undoInFlight.current = false
      }
    }
  }
  const handleUndoRef = useRef(handleUndo)
  handleUndoRef.current = handleUndo

  const handleSidebarAction = (action: DashboardSidebarAction) => {
    switch (action.kind) {
      case 'create-folder':
        if (isAgentInsightsFolderPath(action.parentPath)) return
        setCreateDialog({ kind: 'folder', parentPath: action.parentPath })
        return
      case 'create-dashboard':
        if (isAgentInsightsFolderPath(action.parentPath)) return
        setCreateDialog({ kind: 'dashboard', parentPath: action.parentPath })
        return
      case 'autogen-gwbk':
        navigate('/dashboards/autogenerate')
        return
      case 'rename-folder':
        if (isAgentInsightsFolderPath(action.path)) return
        setRenameDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'rename-dashboard':
        setRenameDialog({
          kind: 'dashboard',
          path: action.dashboard.path,
          name: action.dashboard.name,
          dashboard: action.dashboard
        })
        return
      case 'move-folder':
        if (isAgentInsightsFolderPath(action.path)) return
        setMoveDialog({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'move-dashboard':
        setMoveDialog({
          kind: 'dashboard',
          path: action.dashboard.path,
          name: action.dashboard.name,
          dashboard: action.dashboard
        })
        return
      case 'drop-folder':
        if (isAgentInsightsFolderPath(action.fromPath)) return
        if (isAgentInsightsFolderPath(action.toParentPath)) return
        void moveFolderToParent(action.fromPath, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'drop-dashboard':
        if (isAgentInsightsFolderPath(action.toParentPath)) return
        void moveDashboardToParent(action.dashboard, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'copy-folder':
        if (isAgentInsightsFolderPath(action.path)) return
        clipboard.copy({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'cut-folder':
        if (isAgentInsightsFolderPath(action.path)) return
        clipboard.cut({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'copy-dashboard':
        clipboard.copy({
          kind: 'item',
          id: action.dashboard.id,
          path: action.dashboard.path,
          name: action.dashboard.name
        })
        return
      case 'cut-dashboard':
        clipboard.cut({
          kind: 'item',
          id: action.dashboard.id,
          path: action.dashboard.path,
          name: action.dashboard.name
        })
        return
      case 'paste':
        if (isAgentInsightsFolderPath(action.parentPath)) return
        void handlePaste(action.parentPath)
        return
      case 'undo':
        void handleUndo()
        return
      case 'delete-folder':
        if (isAgentInsightsFolderPath(action.path)) return
        setDeleteDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'delete-dashboard':
        setDeleteDialog({
          kind: 'dashboard',
          path: action.dashboard.path,
          name: action.dashboard.name,
          dashboard: action.dashboard
        })
        return
    }
  }

  const handleCreate = async ({ name, path }: { name: string; path: string }) => {
    if (!createDialog) return
    if (createDialog.kind === 'folder') {
      const created = await createFolder({ name, path }).unwrap()
      undo.push({
        op: 'remove-folder',
        label: `Undo create of "${name}"`,
        atPath: created.path,
        createdPaths: [created.path]
      })
      setExpanded((prev) => expandAncestors(prev, created.path))
      handleSelectFolder(created.path)
    } else {
      const created = await createDashboard({ name, path, code: STARTER_CODE }).unwrap()
      undo.push({
        op: 'remove-dashboard',
        label: `Undo create of "${name}"`,
        id: created.id,
        atPath: created.path,
        expectCode: STARTER_CODE
      })
      setExpanded((prev) => expandAncestors(prev, parentPathOf(created.path)))
      handleSelectDashboard(created.id)
    }
  }

  const handleRename = async ({ name, path }: { name: string; path?: string }) => {
    if (!renameDialog) return
    if (renameDialog.kind === 'folder') {
      const fromPath = renameDialog.path
      const toPath = path ?? fromPath
      if (renameDialog.folder?.id && toPath === fromPath) {
        await renameFolder({ id: renameDialog.folder.id, input: { name } }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          folderId: renameDialog.folder.id,
          atPath: fromPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        return
      }
      if (toPath !== fromPath) {
        await moveFolder({ fromPath, toPath, name }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          ...(renameDialog.folder?.id !== undefined ? { folderId: renameDialog.folder.id } : {}),
          atPath: toPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        afterFolderMoved(fromPath, toPath)
      }
      return
    }
    if (renameDialog.kind === 'dashboard' && renameDialog.dashboard) {
      const input = path !== undefined ? { name, path } : { name }
      const updated = await renameDashboard({
        id: renameDialog.dashboard.id,
        input
      }).unwrap()
      undo.push({
        op: 'dashboard-relocate',
        label: `Undo rename of "${renameDialog.name}"`,
        id: renameDialog.dashboard.id,
        atPath: updated.path,
        toPath: renameDialog.path,
        toName: renameDialog.name
      })
      if (updated.path !== renameDialog.path) {
        setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
        navigate(dashboardHref(updated.path), { replace: true })
      }
    }
  }

  const handleDelete = async () => {
    if (!deleteDialog) return
    if (deleteDialog.kind === 'folder') {
      const snapshot = snapshotFolderDelete(deleteDialog.path, folders, dashboards)
      if (deleteDialog.folder?.id) {
        await deleteFolder(deleteDialog.folder.id).unwrap()
      } else {
        await deleteFolderByPath({ path: deleteDialog.path }).unwrap()
      }
      undo.push({
        op: 'restore-subtree',
        label: `Undo delete of "${deleteDialog.name}"`,
        rootPath: deleteDialog.path,
        folders: snapshot.folders,
        dashboards: snapshot.dashboards
      })
      goHomeIfUnder(deleteDialog.path)
      return
    }
    if (deleteDialog.kind === 'dashboard' && deleteDialog.dashboard) {
      const dashboard = deleteDialog.dashboard
      await deleteDashboard(dashboard.id).unwrap()
      undo.push({
        op: 'restore-dashboard',
        label: `Undo delete of "${dashboard.name}"`,
        name: dashboard.name,
        path: dashboard.path,
        code: dashboard.code,
        ...(dashboard.writebackToolIds && dashboard.writebackToolIds.length > 0
          ? { writebackToolIds: dashboard.writebackToolIds }
          : {})
      })
      if (selection.kind === 'dashboard' && selection.id === dashboard.id) {
        navigate(DASHBOARDS_HOME, { replace: true })
      }
    }
  }

  const handleMove = async (destinationFolderPath: string) => {
    if (!moveDialog) return
    if (moveDialog.kind === 'folder') {
      await moveFolderToParent(moveDialog.path, destinationFolderPath)
      return
    }
    if (moveDialog.dashboard) {
      await moveDashboardToParent(moveDialog.dashboard, destinationFolderPath)
    }
  }

  const dialogOpen =
    createDialog !== null ||
    renameDialog !== null ||
    deleteDialog !== null ||
    moveDialog !== null ||
    actionError !== null

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogOpen) return
      const target = event.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
        if (target.closest('.monaco-editor')) return
      }

      const mod = event.metaKey || event.ctrlKey
      if (mod && !event.altKey && !event.shiftKey) {
        const key = event.key.toLowerCase()
        if (key === 'c') {
          if (hasTextSelection()) return
          if (selection.kind === 'folder' && !isAgentInsightsFolderPath(selection.path)) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === selection.path)
            clipboard.copy({
              kind: 'folder',
              path: selection.path,
              name: folder?.name ?? leafSegment(selection.path)
            })
          } else if (selection.kind === 'dashboard' && selectedDashboard) {
            event.preventDefault()
            clipboard.copy({
              kind: 'item',
              id: selectedDashboard.id,
              path: selectedDashboard.path,
              name: selectedDashboard.name
            })
          }
          return
        }
        if (key === 'x') {
          if (selection.kind === 'folder' && !isAgentInsightsFolderPath(selection.path)) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === selection.path)
            clipboard.cut({
              kind: 'folder',
              path: selection.path,
              name: folder?.name ?? leafSegment(selection.path)
            })
          } else if (selection.kind === 'dashboard' && selectedDashboard) {
            event.preventDefault()
            clipboard.cut({
              kind: 'item',
              id: selectedDashboard.id,
              path: selectedDashboard.path,
              name: selectedDashboard.name
            })
          }
          return
        }
        if (key === 'v') {
          if (clipboard.entry === null) return
          event.preventDefault()
          void handlePasteRef.current(defaultParentForHeaderCreate)
          return
        }
        if (key === 'z') {
          if (undo.entries.length === 0) return
          event.preventDefault()
          void handleUndoRef.current()
          return
        }
      }

      if (event.key === 'Escape' && clipboard.entry?.mode === 'cut') {
        event.preventDefault()
        clipboard.clear()
        return
      }

      if (selection.kind === 'folder') {
        if (isAgentInsightsFolderPath(selection.path)) return
        const folder = folders.find((row) => row.path === selection.path)
        const name = folder?.name ?? leafSegment(selection.path)
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({ kind: 'folder', path: selection.path, name, folder })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({ kind: 'folder', path: selection.path, name, folder })
        }
        return
      }
      if (selection.kind === 'dashboard' && selectedDashboard) {
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({
            kind: 'dashboard',
            path: selectedDashboard.path,
            name: selectedDashboard.name,
            dashboard: selectedDashboard
          })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({
            kind: 'dashboard',
            path: selectedDashboard.path,
            name: selectedDashboard.name,
            dashboard: selectedDashboard
          })
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    clipboard,
    defaultParentForHeaderCreate,
    dialogOpen,
    folders,
    selectedDashboard,
    selection,
    undo.entries.length
  ])

  const moveDisabledPaths = useMemo(() => {
    const blocked = new Set<string>([AGENT_INSIGHTS_FOLDER_PATH])
    if (moveDialog?.kind === 'folder') {
      for (const path of subtreeFolderPaths(moveDialog.path, folders, dashboards)) {
        blocked.add(path)
      }
    }
    return blocked
  }, [moveDialog, folders, dashboards])

  return (
    /* [contain:size] keeps the explorer from inflating AppLayout's outlet
       wrapper (same Docs/Tags pattern): without it, min-height:auto grows
       with the editor and the collapse handle centers on the whole page. */
    <div className="flex h-full min-h-0 flex-col overflow-hidden [contain:size]">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <DashboardTreeSidebar
          folders={folders}
          dashboards={dashboards}
          selection={selection}
          onSelectFolder={handleSelectFolder}
          onSelectDashboard={handleSelectDashboard}
          expanded={expanded}
          onToggle={handleToggle}
          onAction={handleSidebarAction}
          filter={filter}
          onFilterChange={setFilter}
          isLoading={isLoading}
          topSlot={tabsSlot}
          createParentPath={defaultParentForHeaderCreate}
          clipboard={clipboard.entry}
          undoLabel={undo.entries.at(-1)?.label ?? null}
          canAutogenerate={canAutogenerate}
        />

        <div className="relative z-0 min-w-0 flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading dashboards…
            </div>
          ) : selectedDashboard ? (
            <DashboardEditor
              dashboard={selectedDashboard}
              mode={editorMode}
              onModeChange={setEditorMode}
              onRename={() =>
                setRenameDialog({
                  kind: 'dashboard',
                  path: selectedDashboard.path,
                  name: selectedDashboard.name,
                  dashboard: selectedDashboard
                })
              }
              onMove={() =>
                setMoveDialog({
                  kind: 'dashboard',
                  path: selectedDashboard.path,
                  name: selectedDashboard.name,
                  dashboard: selectedDashboard
                })
              }
              onDelete={() =>
                setDeleteDialog({
                  kind: 'dashboard',
                  path: selectedDashboard.path,
                  name: selectedDashboard.name,
                  dashboard: selectedDashboard
                })
              }
            />
          ) : deniedDashboardPath ? (
            <RequestAccessPanel
              resourceType="dashboard"
              path={deniedDashboardPath}
              onAccessGranted={() => {
                void dashboardsQuery.refetch()
              }}
              notFoundMessage="Dashboard not found."
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
              <LayoutDashboard className="size-8 text-ink-3" aria-hidden />
              <p className="m-0 text-sm text-ink-3">
                {folders.length === 0 && dashboards.length === 0
                  ? 'No dashboards or folders yet. Create one to get started.'
                  : routeSelection.kind === 'item'
                    ? 'Dashboard not found.'
                    : 'Select a dashboard from the sidebar.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <CreateItemDialog
        open={createDialog !== null}
        kind={createDialog?.kind ?? 'folder'}
        defaultParentPath={createDialog?.parentPath ?? defaultParentForHeaderCreate}
        folders={folders}
        dashboards={dashboards}
        disabledPaths={new Set([AGENT_INSIGHTS_FOLDER_PATH])}
        onClose={() => setCreateDialog(null)}
        onSubmit={handleCreate}
      />

      <RenameItemDialog
        open={renameDialog !== null}
        kind={renameDialog?.kind ?? 'folder'}
        currentName={renameDialog?.name ?? ''}
        path={renameDialog?.path ?? ''}
        rewriteLeafPath
        onClose={() => setRenameDialog(null)}
        onSubmit={handleRename}
      />

      <MoveItemDialog
        open={moveDialog !== null}
        kind={moveDialog?.kind ?? 'dashboard'}
        name={moveDialog?.name ?? ''}
        currentPath={moveDialog?.path ?? ''}
        folders={folders}
        dashboards={dashboards}
        disabledPaths={moveDisabledPaths}
        onClose={() => setMoveDialog(null)}
        onSubmit={handleMove}
      />

      <DeleteItemDialog
        open={deleteDialog !== null}
        kind={deleteDialog?.kind ?? 'folder'}
        name={deleteDialog?.name ?? ''}
        path={deleteDialog?.path ?? ''}
        descendantCount={
          deleteDialog?.kind === 'folder'
            ? countPrefixDescendants(deleteDialog.path, folders, dashboards)
            : 0
        }
        recursive={deleteDialog?.kind === 'folder'}
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDelete}
      />

      <ActionErrorDialog
        open={actionError !== null}
        title={actionError?.title}
        message={actionError?.message ?? ''}
        onClose={() => setActionError(null)}
      />
    </div>
  )
}

export default DashboardsPage
